/**
 * useSchemaForm — хук «Форма по схеме» для AyoCore.
 *
 * Принцип работы:
 * 1) Принимает объектную TypeBox‑схему с AYA‑метаданными полей (FieldPropertiesSchema).
 * 2) Вычисляет начальные значения (defaults) по типу поля и nullable‑состоянию.
 * 3) Создаёт форму Mantine (useForm) и настраивает валидацию через TypeBox (Value.Check/Errors).
 * 4) Для каждого поля генерирует React‑компонент с типобезопасным доступом к значениям T[K].
 * 5) По $fieldType выбирает визуальный компонент (TextInput, NumberInput, Select, Date/Time и т.п.).
 * 6) Нормализует значения для UX и валидации: пустые строки → null в nullable, строки дат → Date, HH:MM:SS → HH:MM и т.д.
 * 7) Поддерживает RepositorySelect для связей (repo‑поля) с конвертацией значения.
 *
 * Что получает разработчик:
 * - Быстрый рендер форм «из коробки» по схеме (конвенция > конфигурация).
 * - Сильная типобезопасность (от схемы до значений формы T), без any.
 * - Единая валидация и нормализация данных, готовые кнопки и сообщения, fail‑fast на опечатки имён полей.
 */
import React, { useState, JSX } from 'react';
import { Value } from '@sinclair/typebox/value';
import { SetErrorFunction, ValueErrorType } from '@sinclair/typebox/errors';
import { TSchema } from '@sinclair/typebox';
import { useForm, UseFormReturnType } from '@mantine/form';

/**
 * Русскоязычные сообщения валидации TypeBox.
 *
 * Для nullable полей (Union([T, Null])) — валидируем против внутреннего типа,
 * чтобы получить конкретную ошибку вместо "Expected union value".
 */
SetErrorFunction(err => {
  const schema = err.schema as Record<string, unknown>;

  // Кастомное сообщение из схемы ($error или error)
  if (typeof schema.$error === 'string') return schema.$error;
  if (typeof schema.error === 'string') return schema.error;

  // Для nullable Union — выводим осмысленное сообщение вместо "Expected union value"
  // Без рекурсивного Value.Errors — определяем по свойствам внутреннего типа
  if (err.errorType === ValueErrorType.Union && Array.isArray(schema.anyOf)) {
    const inner = (schema.anyOf as Record<string, unknown>[]).find(
      s => s.type !== 'null' && s.type !== undefined
    );
    if (inner) {
      if (inner.type === 'string') {
        if (inner.minLength && String(err.value ?? '').length < Number(inner.minLength))
          return `Минимальная длина: ${inner.minLength}`;
        if (inner.maxLength && String(err.value ?? '').length > Number(inner.maxLength))
          return `Максимальная длина: ${inner.maxLength}`;
        return 'Ожидается строка';
      }
      if (inner.type === 'number' || inner.type === 'integer') return 'Ожидается число';
    }
    return 'Некорректное значение';
  }

  switch (err.errorType) {
    // Строки
    case ValueErrorType.String:
      return 'Ожидается строка';
    case ValueErrorType.StringMinLength:
      return `Минимальная длина: ${schema.minLength}`;
    case ValueErrorType.StringMaxLength:
      return `Максимальная длина: ${schema.maxLength}`;
    case ValueErrorType.StringPattern:
      return 'Значение не соответствует формату';
    case ValueErrorType.StringFormat:
      return `Ожидается формат «${schema.format}»`;

    // Числа
    case ValueErrorType.Number:
      return 'Ожидается число';
    case ValueErrorType.NumberMinimum:
      return `Минимальное значение: ${schema.minimum}`;
    case ValueErrorType.NumberMaximum:
      return `Максимальное значение: ${schema.maximum}`;
    case ValueErrorType.NumberExclusiveMinimum:
      return `Значение должно быть больше ${schema.exclusiveMinimum}`;
    case ValueErrorType.NumberExclusiveMaximum:
      return `Значение должно быть меньше ${schema.exclusiveMaximum}`;
    case ValueErrorType.NumberMultipleOf:
      return `Значение должно быть кратно ${schema.multipleOf}`;

    // Целые числа
    case ValueErrorType.Integer:
      return 'Ожидается целое число';
    case ValueErrorType.IntegerMinimum:
      return `Минимальное значение: ${schema.minimum}`;
    case ValueErrorType.IntegerMaximum:
      return `Максимальное значение: ${schema.maximum}`;
    case ValueErrorType.IntegerExclusiveMinimum:
      return `Значение должно быть больше ${schema.exclusiveMinimum}`;
    case ValueErrorType.IntegerExclusiveMaximum:
      return `Значение должно быть меньше ${schema.exclusiveMaximum}`;
    case ValueErrorType.IntegerMultipleOf:
      return `Значение должно быть кратно ${schema.multipleOf}`;

    // Массивы
    case ValueErrorType.Array:
      return 'Ожидается массив';
    case ValueErrorType.ArrayMinItems:
      return `Минимум элементов: ${schema.minItems}`;
    case ValueErrorType.ArrayMaxItems:
      return `Максимум элементов: ${schema.maxItems}`;
    case ValueErrorType.ArrayUniqueItems:
      return 'Элементы должны быть уникальными';
    case ValueErrorType.ArrayContains:
      return 'Массив должен содержать подходящий элемент';

    // Объекты
    case ValueErrorType.Object:
      return 'Ожидается объект';
    case ValueErrorType.ObjectRequiredProperty:
      return 'Обязательное поле';
    case ValueErrorType.ObjectAdditionalProperties:
      return 'Лишнее свойство';
    case ValueErrorType.ObjectMinProperties:
      return `Минимум свойств: ${schema.minProperties}`;
    case ValueErrorType.ObjectMaxProperties:
      return `Максимум свойств: ${schema.maxProperties}`;

    // Прочие типы
    case ValueErrorType.Boolean:
      return 'Ожидается логическое значение';
    case ValueErrorType.Null:
      return 'Ожидается пустое значение';
    case ValueErrorType.Date:
      return 'Ожидается дата';
    case ValueErrorType.Undefined:
      return 'Значение не определено';
    case ValueErrorType.Literal:
      return `Ожидается ${typeof schema.const === 'string' ? `«${schema.const}»` : schema.const}`;
    case ValueErrorType.Union:
      return 'Некорректное значение';
    case ValueErrorType.Never:
      return 'Недопустимое значение';
    case ValueErrorType.RegExp:
      return 'Значение не соответствует формату';

    default:
      return 'Ошибка валидации';
  }
});
import { TextInput, Textarea, NumberInput, Select, Button, Alert, Checkbox } from '@mantine/core';
import type { ButtonProps } from '@mantine/core';
import { TimePicker } from '@mantine/dates';
import { AyoDateInput } from '@ayocore/platform/client/components/AyoDateInput';
import { IconCheck, IconX } from '@tabler/icons-react';
import logger from '@ayocore/platform/common/helpers/logger';
import { RepositorySelect } from '@ayocore/platform/client/components/RepositorySelect';
import type { RpcRepoClient } from '@ayocore/platform/server/rpc/types';
import {
  FieldProperty,
  FieldPropertiesSchema,
} from '@ayocore/platform/common/helpers/schema-types';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

/**
 * Преобразует значение поля даты в Date, устойчивый к смещению дня
 * при round-trip через сервер и БД (timestamp without time zone).
 *
 * Для строк вида "YYYY-MM-DD" возвращаем точку UTC-полдень выбранного дня
 * (12:00Z): в любой таймзоне ±12ч локальный календарный день совпадает,
 * поэтому после JSON-сериализации клиентом, записи pg как timestamp без TZ
 * и обратного чтения — день остаётся прежним независимо от TZ сервера и клиента.
 *
 * Для уже готовых Date и полных ISO-строк с временем сохраняем семантику
 * момента времени (просто new Date(...)).
 */
function parseLocalDate(value: string | Date): Date {
  if (value instanceof Date) {
    // Если это «чистая» локальная полночь (дата без времени), нормализуем
    // в UTC-полдень того же локального дня, чтобы избежать day-drift через toISOString.
    const isLocalMidnight =
      value.getHours() === 0 &&
      value.getMinutes() === 0 &&
      value.getSeconds() === 0 &&
      value.getMilliseconds() === 0;
    if (isLocalMidnight) {
      return new Date(
        Date.UTC(value.getFullYear(), value.getMonth(), value.getDate(), 12, 0, 0, 0)
      );
    }
    return value;
  }
  const strict = dayjs(value, 'YYYY-MM-DD', true);
  if (strict.isValid()) {
    return new Date(Date.UTC(strict.year(), strict.month(), strict.date(), 12, 0, 0, 0));
  }
  return new Date(value);
}

/**
 * Унифицированные пропсы для кнопок SchemaForm.
 * Основаны на Mantine ButtonProps, исключают children/type,
 * добавляют label и явный onClick для предсказуемости типов.
 */
type ButtonLikeProps = Omit<ButtonProps, 'children' | 'type' | 'onClick'> & {
  label?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
};

/**
 * Pure React Convention Over Configuration Schema Form
 *
 * Features:
 * - Auto-generates React components from TypeBox schema
 * - Smart field type detection (string → TextInput, boolean → Switch, etc.)
 * - Built-in validation, error/success handling
 * - Zero configuration needed, maximum convention
 * - Custom overrides when needed
 * - Schema title/description support for better UX
 *
 * Fixed: Stable components created outside hook to prevent re-creation
 */

/** Дополнительная обязательность поля формы; ограничения схемы сохраняются. */
export type SchemaFormFields<T> = Partial<Record<Extract<keyof T, string>, { required?: boolean }>>;

/** Опции useSchemaForm. T — объект значений формы. */
export interface UseSchemaFormOptions<T extends Record<string, unknown>> {
  initialValues?: Partial<T>;
  fields?: SchemaFormFields<T>;
  customValidation?: (values: T) => Record<string, string>;
  onSubmitSuccess?: (result: T) => void;
  onSubmitError?: (error: unknown) => void;
  // Validation trigger controls
  validateOnChange?: boolean; // default: true
  validateOnBlur?: boolean; // default: false
  /** Скрыть description из схемы для всех полей формы. Явный description-проп на поле всё равно работает */
  hideDescriptions?: boolean;
}

/**
 * Компонент поля формы с типобезопасным доступом к значению T[K].
 * Содержит:
 * - вызов как JSX-компонента для рендера
 * - props: «сырые» input-пропсы из Mantine getInputProps
 * - value: текущее значение поля точного типа T[K]
 */
export interface FieldComponent<
  T extends Record<string, unknown> = Record<string, unknown>,
  K extends keyof T = keyof T,
> {
  (props?: Record<string, unknown>): JSX.Element;
  // Can be used as JSX component: <form.field.name />
  // Or as function: form.field.name()
  // Backward compatibility properties for existing code
  props: ReturnType<UseFormReturnType<T>['getInputProps']> & { withAsterisk?: boolean };
  value: T[K];
  error: string | undefined;
  name: string;
}

/**
 * Возвращаемый интерфейс формы: состояние, методы, типизированные поля,
 * кнопки (submit/cancel) и компонент сообщений.
 */
export interface SchemaFormReturn<T extends Record<string, unknown>> extends UseFormReturnType<T> {
  // Enhanced submission with error/success handling - now properly typed to match Mantine's onSubmit
  handleSubmit: (
    submitFn: (values: T) => Promise<unknown> | unknown
  ) => (event?: React.FormEvent<HTMLFormElement>) => void;

  // State management
  isSubmitting: boolean;
  error: string | null;
  success: string | null;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
  clearMessages: () => void;

  // Clean data loading
  setInitialData: (data: Partial<T>) => void;

  // Convention over configuration - auto-generated React components
  Field: {
    [K in Extract<keyof T, string>]: FieldComponent<T, K>;
  };

  // Simple button components - just submit and cancel
  buttons: {
    submit: React.ComponentType<ButtonLikeProps>;
    cancel: React.ComponentType<ButtonLikeProps>;
  };

  // Messages component for displaying success/error alerts
  Messages: React.ComponentType<Record<string, unknown>>;

  // Флаг: показывать ли ошибки валидации (true после первого сабмита)
  showValidationErrors: boolean;
}

/**
 * Извлекает метаданные поля (title, description) из объектной схемы с метаданными полей.
 */
function extractFieldMetadata(
  schema: FieldPropertiesSchema,
  fieldName: string
): { title?: string; description?: string } {
  const properties = schema.properties;
  if (!properties || !properties[fieldName]) {
    return {};
  }

  const fieldSchema = properties[fieldName];

  // Handle nullable fields (anyOf with null type)
  if (fieldSchema.anyOf) {
    const nonNullType = fieldSchema.anyOf.find(t => t.type !== 'null');
    const hasNullType = fieldSchema.anyOf.some(t => t.type === 'null');

    // For nullable fields, prefer union-level metadata first (it may be set by processRelations),
    // then fall back to the non-null inner type.
    if (nonNullType && hasNullType) {
      return {
        title: fieldSchema.title ?? nonNullType.title,
        description: fieldSchema.description ?? nonNullType.description,
      };
    }
  }

  return {
    title: fieldSchema.title,
    description: fieldSchema.description,
  };
}

/**
 * Формирует человекочитаемую метку на основе title или имени поля.
 * Приоритет: schema.title > сгенерированная из имени.
 */
function createFieldLabel(schema: FieldPropertiesSchema, fieldName: string): string {
  const metadata = extractFieldMetadata(schema, fieldName);

  // Use schema title if available
  if (metadata.title) {
    return metadata.title;
  }

  // Fallback to generated label from field name
  return fieldName
    .replace(/([A-Z])/g, ' $1') // camelCase → "camel Case"
    .replace(/^./, str => str.toUpperCase()) // capitalize first letter
    .replace(/_/g, ' ') // snake_case → "snake case"
    .trim();
}

/**
 * Detect React component type from AYA field types using $fieldType metadata
 * Clean approach: Use explicit field type metadata instead of complex type guessing
 */
function detectComponentType(schema: FieldPropertiesSchema, fieldName: string): string {
  const properties = schema.properties;
  if (!properties || !properties[fieldName]) {
    throw new Error(`Field '${fieldName}' not found in schema`);
  }

  const fieldSchema = properties[fieldName];

  // Check for AYA field type metadata first
  if (fieldSchema.$fieldType) {
    return mapFieldTypeToComponent(fieldSchema.$fieldType);
  }

  // Handle nullable fields - check the non-null type for $fieldType
  if (fieldSchema.anyOf) {
    const nonNullType = fieldSchema.anyOf.find(t => t.type !== 'null');
    if (nonNullType && nonNullType.$fieldType) {
      return mapFieldTypeToComponent(nonNullType.$fieldType);
    }

    // Check if it's an enum union (for backward compatibility)
    const options = fieldSchema.anyOf;
    const isEnumVariant = (opt: TSchema): boolean => {
      const o = opt as unknown as Record<string, unknown>;
      return 'const' in o || 'enum' in o;
    };
    if (options?.every(isEnumVariant)) {
      return 'select';
    }
  }

  // Fallback: render as text and warn
  logger.warn(
    `SchemaForm: Field '${fieldName}' has no $fieldType metadata; rendering as text. Update schema to use AYA field types.`
  );
  return 'text';
}

/**
 * Map AYA field types to React components
 */
export function mapFieldTypeToComponent(fieldType: string): string {
  switch (fieldType) {
    case 'varchar': // StringField
      return 'text';
    case 'text': // TextField
      return 'textarea';
    case 'integer': // IntegerField
      return 'number';
    case 'bigint': // BigIntField (64-bit; в безопасном диапазоне число — обычный NumberInput)
      return 'number';
    case 'decimal': // DecimalField
      return 'number';
    case 'timestamp': // TimestampField
      return 'date';
    case 'date': // DateField (календарная дата, хранится строкой YYYY-MM-DD)
      return 'dateonly';
    case 'time': // TimeField
      return 'time';
    case 'boolean': // BooleanField
      return 'checkbox';
    case 'jsonb': // JsonField
      return 'json';
    case 'uuid': // UuidField
      return 'text';
    case 'enum': // EnumField
      return 'select';
    // Array field types
    case 'text[]':
    case 'varchar[]':
    case 'integer[]':
    case 'decimal[]':
    case 'numeric[]': // DecimalField arrays
    case 'boolean[]':
      return 'array';
    default:
      throw new Error(
        `Unknown field type: ${fieldType}. Please add mapping in mapFieldTypeToComponent(). Found: '${fieldType}'`
      );
  }
}

/**
 * Get default value for AYA field types
 */
export function getDefaultValueForFieldType(fieldType: string, isNullable: boolean) {
  if (isNullable) {
    // Nullable fields start as null (except booleans which need a value)
    return fieldType === 'boolean' ? false : null;
  }

  // Required fields get appropriate defaults
  switch (fieldType) {
    case 'varchar': // StringField
    case 'text': // TextField
    case 'uuid': // UuidField
      return '';
    case 'integer': // IntegerField
    case 'bigint': // BigIntField
    case 'decimal': // DecimalField
      return ''; // Empty string for controlled inputs
    case 'timestamp': // TimestampField
      return null; // Dates can start as null
    case 'date': // DateField
      return null; // Календарная дата может стартовать пустой
    case 'boolean': // BooleanField
      return false;
    case 'jsonb': // JsonField
      return '';
    case 'enum': // EnumField
      return '';
    // Array field types
    case 'text[]':
    case 'varchar[]':
    case 'integer[]':
    case 'decimal[]':
    case 'numeric[]': // DecimalField arrays
    case 'boolean[]':
      return [];
    default:
      return '';
  }
}

/**
 * Extract select options from AYA EnumField
 *
 * Опции селекта из $enumValues: прямое enum-поле, nullable-union (anyOf)
 * и legacy const-union. Экспортируется для unit-тестов.
 */
export function extractSelectOptions(
  schema: FieldPropertiesSchema,
  fieldName: string
): Array<{ value: string; label: string }> {
  const properties = schema.properties;
  if (!properties || !properties[fieldName]) return [];

  const fieldSchema = properties[fieldName];

  // Handle EnumField options
  if (fieldSchema.$fieldType === 'enum' && fieldSchema.$enumValues) {
    return fieldSchema.$enumValues.map((value: string) => ({
      value,
      label: value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' '),
    }));
  }

  // Handle nullable EnumField
  const anyOf = fieldSchema.anyOf;
  if (anyOf) {
    type EnumLike = TSchema & { $fieldType?: string; $enumValues?: string[]; type?: string };
    const nonNull = anyOf.find(t => (t as EnumLike).type !== 'null') as EnumLike | undefined;
    if (nonNull?.$fieldType === 'enum' && Array.isArray(nonNull.$enumValues)) {
      return nonNull.$enumValues.map((value: string) => ({
        value,
        label: value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' '),
      }));
    }
    // Backward compatibility: old union approach
    const hasConst = (opt: TSchema): opt is TSchema & { const: string } =>
      'const' in (opt as Record<string, unknown>);
    return anyOf.filter(hasConst).map(opt => ({
      value: (opt as unknown as { const: string }).const,
      label: createFieldLabel(schema, (opt as unknown as { const: string }).const),
    }));
  }

  return [];
}

// Stable field component factory - created outside hook
type BaseFieldProps = {
  label?: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
  withAsterisk?: boolean;
  minRows?: number;
  maxRows?: number;
  repo?: RpcRepoClient;
} & Record<string, unknown>;

function createFieldComponent<
  T extends Record<string, unknown>,
  K extends Extract<keyof T, string>,
>(
  schema: FieldPropertiesSchema,
  fieldName: K,
  getFormProps: () => {
    getInputProps: UseFormReturnType<T>['getInputProps'];
    isSubmitting: boolean;
    showErrors: boolean;
    getValue: () => T[K];
    getError: () => string | undefined;
    hideDescriptions: boolean;
    required: boolean;
  }
): FieldComponent<T, K> {
  const FieldComponent = (props: BaseFieldProps = {}) => {
    // const { getInputProps, isSubmitting, showErrors } = getFormProps();
    const { getInputProps, isSubmitting, hideDescriptions, required } = getFormProps();
    const metadata = extractFieldMetadata(schema, fieldName);
    const label: React.ReactNode = props.label ?? createFieldLabel(schema, fieldName);
    const schemaDescription = hideDescriptions ? undefined : metadata.description;
    const description: React.ReactNode | undefined = props.description ?? schemaDescription;
    const fieldProps = getInputProps(fieldName);
    const selectOptions = extractSelectOptions(schema, fieldName);

    // Handle undefined/null values for React components - ensure always controlled
    const safeFieldProps = {
      ...fieldProps,
      value: fieldProps.value === null || fieldProps.value === undefined ? '' : fieldProps.value,
    };
    // Detect component type early (used below); harmless even if repo branch returns earlier
    const componentType = detectComponentType(schema, fieldName);

    // Special handling for nullable number fields - NumberInput needs empty string for display but null for validation
    if (componentType === 'number') {
      const fieldSchema = schema.properties[fieldName];
      const isNullable = fieldSchema.anyOf && fieldSchema.anyOf.some(t => t.type === 'null');

      if (isNullable) {
        // For nullable numbers: display empty string, but convert empty back to null for validation
        const originalOnChange = fieldProps.onChange;
        safeFieldProps.onChange = (value: string | number | undefined) => {
          if (value === '' || value === undefined) {
            originalOnChange(null);
          } else {
            originalOnChange(value);
          }
        };
      }
    }

    // Special handling for date fields - DateInput returns strings but TypeBox expects Date objects
    if (componentType === 'date') {
      // DateInput value: строка YYYY-MM-DD в локальной таймзоне.
      // Важно: toISOString() даст UTC-дату и в положительных TZ «уедет» на день раньше,
      // поэтому форматируем локально через dayjs.
      safeFieldProps.value = fieldProps.value
        ? fieldProps.value instanceof Date
          ? dayjs(fieldProps.value).format('YYYY-MM-DD')
          : fieldProps.value
        : null;

      // DateInput onChange: строку YYYY-MM-DD кладём в форму как локальную полночь,
      // чтобы последующая сериализация/отображение не смещали день.
      const originalOnChange = fieldProps.onChange;
      safeFieldProps.onChange = (value: string | null) => {
        if (value === null || value === '') {
          originalOnChange(null);
        } else {
          originalOnChange(parseLocalDate(value));
        }
      };
    }

    // Special handling for DateField - календарная дата хранится строкой 'YYYY-MM-DD'.
    // В отличие от timestamp, НЕ конвертируем в Date: AyoDateInput и так работает со строкой,
    // а строковая модель исключает любые TZ-сдвиги дня.
    if (componentType === 'dateonly') {
      safeFieldProps.value = fieldProps.value ?? null;

      const originalOnChange = fieldProps.onChange;
      safeFieldProps.onChange = (value: string | null) => {
        originalOnChange(value === null || value === '' ? null : value);
      };
    }

    // Special handling for time fields - TimePicker expects hh:mm but server sends hh:mm:ss
    if (componentType === 'time') {
      // TimePicker value: convert hh:mm:ss to hh:mm for display
      safeFieldProps.value = fieldProps.value
        ? typeof fieldProps.value === 'string'
          ? fieldProps.value.slice(0, 5)
          : fieldProps.value
        : null;

      // TimePicker onChange: keep as hh:mm string for validation
      const originalOnChange = fieldProps.onChange;
      safeFieldProps.onChange = (value: string | null) => {
        if (value === null || value === '') {
          originalOnChange(null);
        } else {
          // TimePicker returns hh:mm format, keep it as is
          originalOnChange(value);
        }
      };
    }

    // Special handling for repository-based selects (early return, no component type detection)
    if (props.repo) {
      // Convert string value back to number for repository queries (if needed)
      const repositoryProps = {
        ...safeFieldProps,
        ...props,
        label,
        description,
        withAsterisk: required || props.withAsterisk,
        disabled: props.disabled || isSubmitting,
        'data-testid': `field-${fieldName}`,
        repo: props.repo,
      };

      // Handle value conversion for repository selects
      const originalOnChange = fieldProps.onChange;
      repositoryProps.onChange = (value: string | null) => {
        if (value === null || value === '') {
          originalOnChange(null);
        } else {
          // Try to convert to number if the field expects a number
          const numValue = Number(value);
          if (!isNaN(numValue) && String(numValue) === value) {
            originalOnChange(numValue);
          } else {
            originalOnChange(value);
          }
        }
      };

      return <RepositorySelect {...repositoryProps} />;
    }
    const mergedProps = {
      autoComplete: 'new-password',
      ...safeFieldProps,
      ...props,
      label,
      description,
      withAsterisk: required || props.withAsterisk,
      disabled: props.disabled || isSubmitting,
      'data-testid': `field-${fieldName}`,
    };

    // Render appropriate React component
    switch (componentType) {
      case 'textarea': {
        const node = (
          <Textarea
            {...mergedProps}
            autosize
            minRows={props.minRows ?? 1}
            maxRows={props.maxRows ?? 6}
          />
        );
        return node;
      }
      case 'number': {
        const node = <NumberInput {...mergedProps} />;
        return node;
      }
      case 'checkbox': {
        // Mantine Checkbox expects `checked` and onChange -> boolean
        const originalOnChange = fieldProps.onChange;
        const checkboxProps: any = {
          ...props,
          label,
          description,
          withAsterisk: required || props.withAsterisk,
          disabled: props.disabled || isSubmitting,
          'data-testid': `field-${fieldName}`,
          checked: Boolean(fieldProps.value),
          onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
            originalOnChange(event.currentTarget.checked);
          },
        };
        const node = <Checkbox {...checkboxProps} />;
        return node;
      }
      case 'select': {
        const node = <Select {...mergedProps} data={selectOptions} searchable clearable />;
        return node;
      }
      case 'date':
      case 'dateonly': {
        const node = <AyoDateInput {...mergedProps} />;
        return node;
      }
      case 'time':
        // TimePicker returns hh:mm:ss but TimeField expects hh:mm
        return (
          <TimePicker
            {...mergedProps}
            {...safeFieldProps}
            withDropdown
            popoverProps={{
              width: 'auto', // Let it size automatically
            }}
            presets={[
              '08:00',
              '09:00',
              '10:00',
              '11:00',
              '12:00',
              '13:00',
              '14:00',
              '15:00',
              '16:00',
              '17:00',
              '18:00',
              '19:00',
              '20:00',
              '21:00',
              '22:00',
              '23:00',
            ]}
          />
        );
      case 'json': {
        const node = (
          <Textarea
            {...mergedProps}
            autosize
            minRows={3}
            maxRows={10}
            placeholder='Enter valid JSON...'
          />
        );
        return node;
      }
      case 'array': {
        const node = <TextInput {...mergedProps} placeholder='Comma-separated values...' />;
        return node;
      }
      default: {
        // text
        const node = <TextInput {...mergedProps} />;
        return node;
      }
    }
  };

  // Attach compatibility properties for typings and runtime
  Object.defineProperty(FieldComponent, 'props', {
    get: () => ({
      ...getFormProps().getInputProps(fieldName),
      withAsterisk: getFormProps().required || undefined,
    }),
    enumerable: true,
    configurable: true,
  });
  Object.defineProperty(FieldComponent, 'value', {
    get: () => getFormProps().getValue(),
    enumerable: true,
    configurable: true,
  });
  Object.defineProperty(FieldComponent, 'error', {
    get: () => getFormProps().getError(),
    enumerable: true,
    configurable: true,
  });
  Object.defineProperty(FieldComponent, 'name', {
    value: fieldName as string,
    writable: true,
    enumerable: true,
    configurable: true,
  });

  // Set display name for better debugging
  FieldComponent.displayName = `SchemaField_${String(fieldName)}`;

  return FieldComponent as unknown as FieldComponent<T, K>;
}

// Stable button components - created outside hook
function createSubmitButton(getSubmissionState: () => boolean) {
  return (props: ButtonLikeProps = {}) => {
    const isSubmitting = getSubmissionState();
    return (
      <Button type='submit' loading={isSubmitting} data-testid='submit-button' {...props}>
        {props.label || 'Submit'}
      </Button>
    );
  };
}

function createCancelButton(getSubmissionState: () => boolean) {
  return (props: ButtonLikeProps = {}) => {
    const isSubmitting = getSubmissionState();
    return (
      <Button
        variant='light'
        onClick={props.onClick}
        disabled={isSubmitting}
        data-testid='cancel-button'
        {...props}
      >
        {props.label || 'Отмена'}
      </Button>
    );
  };
}

/**
 * Pure React Convention Over Configuration Schema Form Hook
 *
 * Usage - JSX Component Syntax (Recommended):
 *   const form = useSchemaForm(MySchema);
 *   return (
 *     <form onSubmit={form.handleSubmit(submitFn)}>
 *       <form.field.name />
 *       <form.field.email placeholder="Custom placeholder" />
 *       {form.buttons.createCancel()}
 *     </form>
 *   );
 *
 * Usage - Function Call Syntax (Also Supported):
 *   return (
 *     <form onSubmit={form.handleSubmit(submitFn)}>
 *       {form.field.name()}
 *       {form.field.email({ placeholder: "Custom placeholder" })}
 *       {form.buttons.createCancel()}
 *     </form>
 *   );
 */
export function useSchemaForm<T extends Record<string, unknown>>(
  schema: FieldPropertiesSchema,
  options: UseSchemaFormOptions<T> = {}
): SchemaFormReturn<T> {
  const {
    initialValues = {} as Partial<T>,
    fields,
    customValidation,
    onSubmitSuccess,
    onSubmitError,
    validateOnChange = true,
    validateOnBlur = false,
    hideDescriptions = false,
  } = options;

  // Form state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Extract default values from schema using AYA field types
  const defaults: Record<string, unknown> = {};
  const properties = schema.properties || {};

  Object.keys(properties).forEach(fieldName => {
    const fieldSchema = properties[fieldName];

    // Check for explicit default first
    if (fieldSchema.default !== undefined) {
      defaults[fieldName] = fieldSchema.default;
      return;
    }

    // Handle nullable fields - check the non-null type for defaults
    if (fieldSchema.anyOf) {
      const nonNullType = fieldSchema.anyOf.find(t => t.type !== 'null');
      const hasNullType = fieldSchema.anyOf.some(t => t.type === 'null');

      if (nonNullType && hasNullType) {
        // This is a nullable field - check non-null type for default
        if (nonNullType.default !== undefined) {
          defaults[fieldName] = nonNullType.default;
          return;
        }

        // For nullable fields, provide React-compatible defaults based on AYA field type
        const fieldType = nonNullType.$fieldType;
        if (fieldType) {
          defaults[fieldName] = getDefaultValueForFieldType(fieldType, true);
        } else {
          // Fallback for fields without $fieldType
          defaults[fieldName] = null;
        }
        return;
      }
    }

    // For required fields (non-nullable), provide defaults based on AYA field type
    const fieldType = fieldSchema.$fieldType;
    if (fieldType) {
      defaults[fieldName] = getDefaultValueForFieldType(fieldType, false);
    } else {
      // Fallback for fields without $fieldType
      defaults[fieldName] = '';
    }
  });

  // Initialize Mantine form with TypeBox validation
  const form = useForm<T>({
    initialValues: { ...defaults, ...initialValues } as T,
    validateInputOnChange: validateOnChange,
    validateInputOnBlur: validateOnBlur,
    validate: values => {
      const errors: Record<string, string> = {};

      // Normalize form values for TypeBox validation
      // Convert empty strings to null for nullable fields
      const normalizedValues = { ...values };
      const properties = schema.properties || {};

      Object.keys(properties).forEach(fieldName => {
        const fieldSchema = properties[fieldName];
        const fieldValue = normalizedValues[fieldName as keyof T];

        // Helper function to check if a schema is a timestamp field
        const isTimestampField = (s: FieldProperty): boolean => {
          if (s.$fieldType === 'timestamp') return true;
          if (s.anyOf) {
            return s.anyOf.some(t => t.$fieldType === 'timestamp');
          }
          return false;
        };
        // Helper function to check if a schema is a time field
        const isTimeField = (s: FieldProperty): boolean => {
          if (s.$fieldType === 'time') return true;
          if (s.anyOf) {
            return s.anyOf.some(t => t.$fieldType === 'time');
          }
          return false;
        };

        // Check if this is a nullable field (Union with null)
        if (fieldSchema.anyOf) {
          const hasNullType = fieldSchema.anyOf.some(t => t.type === 'null');
          if (hasNullType && (fieldValue === '' || fieldValue === undefined)) {
            // Convert empty strings to null for nullable fields
            (normalizedValues as any)[fieldName] = null;
          }
        }

        // Smart date conversion: Convert ISO strings to Date objects for timestamp fields.
        // Для строк YYYY-MM-DD используем локальный парсер, чтобы избежать UTC-смещения.
        if (isTimestampField(fieldSchema) && typeof fieldValue === 'string' && fieldValue !== '') {
          try {
            const dateObject = parseLocalDate(fieldValue);
            if (!isNaN(dateObject.getTime())) {
              (normalizedValues as any)[fieldName] = dateObject;
            }
          } catch (error) {
            // If parsing fails, keep original value (validation will catch invalid dates)
            logger.error(error);
          }
        }

        // Normalize time strings: trim HH:MM:SS to HH:MM for TimeField
        if (isTimeField(fieldSchema) && typeof fieldValue === 'string' && fieldValue !== '') {
          // Accept both HH:MM and HH:MM:SS; store as HH:MM
          const match = fieldValue.match(/^([0-1][0-9]|2[0-3]):([0-5][0-9])(:[0-5][0-9])?$/);
          if (match) {
            (normalizedValues as any)[fieldName] = `${match[1]}:${match[2]}`;
          }
        }
      });

      // TypeBox schema validation with normalized values
      if (!Value.Check(schema, normalizedValues)) {
        const validationErrors = Array.from(Value.Errors(schema, normalizedValues));
        validationErrors.forEach(error => {
          const fieldName = error.path.substring(1); // Remove leading '/'
          if (fieldName) {
            // Проверяем кастомное сообщение из $error в FieldMeta
            const fieldSchema = (schema as { properties?: Record<string, Record<string, unknown>> })
              .properties?.[fieldName];
            const customError = fieldSchema?.['$error'] as
              | string
              | ((e: { message: string; path: string; schema: Record<string, unknown> }) => string)
              | undefined;

            if (typeof customError === 'function') {
              errors[fieldName] = customError({
                message: error.message,
                path: error.path,
                schema: error.schema as Record<string, unknown>,
              });
            } else if (typeof customError === 'string') {
              errors[fieldName] = customError;
            } else {
              errors[fieldName] = error.message;
            }
          }
        });
      }

      // Custom validation layer
      if (customValidation) {
        const customErrors = customValidation(normalizedValues as T);
        Object.assign(errors, customErrors);
      }

      // Обязательность формы дополняет TypeBox и customValidation, не меняя схему.
      Object.keys(properties).forEach(fieldName => {
        const key = fieldName as Extract<keyof T, string>;
        if (!fields?.[key]?.required) return;
        const value = values[key];
        if (
          value == null ||
          (typeof value === 'string' && value.trim() === '') ||
          (Array.isArray(value) && value.length === 0)
        ) {
          errors[key] = 'Обязательное поле';
        }
      });

      return errors;
    },
  });

  // Enhanced submit handler that properly triggers validation
  const handleSubmit = (submitFn: (values: T) => Promise<any> | any) => {
    return form.onSubmit(
      async values => {
        if (isSubmitting) return; // Prevent double submission

        setIsSubmitting(true);
        setError(null);
        setSuccess(null);
        setShowValidationErrors(true); // Show validation errors on submit attempt

        try {
          // Pre-submit normalization mirroring validation stage
          const normalizedValues: Record<string, unknown> = { ...values };
          const schemaProps = schema.properties;
          const isTimestampField = (s: FieldProperty | undefined) =>
            !!(
              s?.$fieldType === 'timestamp' ||
              (s?.anyOf && s.anyOf.some(t => t.$fieldType === 'timestamp'))
            );
          const isTimeField = (s: FieldProperty | undefined) =>
            !!(
              s?.$fieldType === 'time' ||
              (s?.anyOf && s.anyOf.some(t => t.$fieldType === 'time'))
            );
          Object.keys(schemaProps).forEach(fieldName => {
            const fieldSchema = schemaProps[fieldName];
            const fieldValue = normalizedValues[fieldName];
            // Timestamp: convert ISO string to Date (локально для YYYY-MM-DD, иначе стандартно)
            if (
              isTimestampField(fieldSchema) &&
              typeof fieldValue === 'string' &&
              fieldValue !== ''
            ) {
              const d = parseLocalDate(fieldValue);
              if (!isNaN(d.getTime())) normalizedValues[fieldName] = d;
            }
            // Time: trim HH:MM:SS -> HH:MM
            if (isTimeField(fieldSchema) && typeof fieldValue === 'string' && fieldValue !== '') {
              const match = fieldValue.match(/^([0-1][0-9]|2[0-3]):([0-5][0-9])(:[0-5][0-9])?$/);
              if (match) normalizedValues[fieldName] = `${match[1]}:${match[2]}`;
            }
          });

          const result = await submitFn(normalizedValues as T);
          if (onSubmitSuccess) onSubmitSuccess(result);
          setSuccess('Операция успешно выполнена');
        } catch (err) {
          // Извлекаем сообщение об ошибке из разных источников
          let errorMessage = 'Произошла ошибка';

          if (err instanceof Error) {
            // Проверяем, есть ли ответ от сервера (axios-ошибка)
            const axiosError = err as any;
            if (axiosError.response?.data?.error?.message) {
              // RPC ошибка в формате { error: { message: string } }
              errorMessage = axiosError.response.data.error.message;
            } else if (axiosError.response?.data?.message) {
              // Альтернативный формат ошибки
              errorMessage = axiosError.response.data.message;
            } else {
              // Используем стандартное сообщение об ошибке
              errorMessage = err.message;
            }
          }

          setError(errorMessage);
          if (onSubmitError) onSubmitError(err);
          logger.error('Form submission error:', err);
        } finally {
          setIsSubmitting(false);
        }
      },
      validationErrors => {
        logger.error(validationErrors);
        setShowValidationErrors(true);
      }
    );
  };

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const reset = () => {
    form.reset();
    setShowValidationErrors(false); // Hide validation errors on reset
    clearMessages();
  };

  // Clean method to set initial data - handles everything internally
  const setInitialData = React.useCallback(
    (data: Partial<T>) => {
      // Set all values at once to prevent multiple re-renders
      form.setValues(data as T);
      // Reset dirty state - this is now the "initial" data
      form.resetDirty(data as T);
      // Reset touched state for clean start
      form.resetTouched();
    },
    [form]
  );

  // Auto-generate field components with fail-fast validation - memoized to prevent re-renders and focus loss
  // Стабильные компоненты читают актуальные значения и настройки без перемонтирования.
  const currentForm = React.useRef({
    form,
    isSubmitting,
    showValidationErrors,
    hideDescriptions,
    fields,
  });
  currentForm.current = { form, isSubmitting, showValidationErrors, hideDescriptions, fields };

  const fieldComponents = React.useMemo(() => {
    const components: Record<string, unknown> = {};
    const schemaProperties = schema.properties || {};
    const allFieldNames = [
      ...Object.keys(currentForm.current.form.values),
      ...Object.keys(schemaProperties),
    ];
    const uniqueFieldNames = [...new Set(allFieldNames)];

    // Create components for existing schema fields
    uniqueFieldNames.forEach(fieldName => {
      // Create the field component as a React component
      const key = fieldName as Extract<keyof T, string>;
      const FieldComponent = createFieldComponent<T, Extract<keyof T, string>>(schema, key, () => ({
        getInputProps: currentForm.current.form.getInputProps,
        isSubmitting: currentForm.current.isSubmitting,
        showErrors: currentForm.current.showValidationErrors,
        getValue: () => currentForm.current.form.values[key],
        getError: () => {
          const error = currentForm.current.showValidationErrors
            ? currentForm.current.form.errors[key]
            : undefined;
          return typeof error === 'string' ? error : undefined;
        },
        hideDescriptions: currentForm.current.hideDescriptions,
        required: currentForm.current.fields?.[key]?.required === true,
      }));

      components[fieldName] = FieldComponent;
    });

    // 🎯 FAIL FAST: Wrap in Proxy to catch access to non-existent fields
    const proxy = new Proxy(components, {
      get(target, fieldName: string | symbol) {
        // Allow access to existing fields
        if (typeof fieldName === 'string' && target[fieldName]) {
          return target[fieldName];
        }

        // Allow access to standard object properties
        if (typeof fieldName === 'symbol' || fieldName in Object.prototype) {
          return Reflect.get(target, fieldName);
        }

        // 🚨 FAIL FAST: Clear error for non-existent fields
        const availableFields = Object.keys(schemaProperties).sort();
        const errorMessage = [
          `❌ SchemaForm Error: Field '${fieldName}' does not exist in schema!`,
          ``,
          `📋 Available fields: ${availableFields.join(', ')}`,
          ``,
          `🔧 Fix: Either:`,
          `   1. Add '${fieldName}' to your schema definition`,
          `   2. Remove '<SchemaForm.Field.${fieldName} />' from your form`,
          `   3. Check for typos in field name`,
          ``,
          `🎯 Schema file: Check your defineSchema() in the schema file`,
        ].join('\n');

        logger.error(errorMessage);
        throw new Error(errorMessage);
      },

      // Also handle property enumeration for debugging
      ownKeys(target) {
        return Object.keys(target);
      },

      has(target, fieldName) {
        return typeof fieldName === 'string' && fieldName in target;
      },
    });
    return proxy as unknown as { [K in Extract<keyof T, string>]: FieldComponent<T, K> };
  }, [schema]);

  // Auto-generated button components - memoized to prevent re-renders
  const buttons = React.useMemo(
    () => ({
      submit: createSubmitButton(() => isSubmitting),
      cancel: createCancelButton(() => isSubmitting),
    }),
    [isSubmitting]
  );

  //TODO: сейчас сообщения эти сдвигают форму, необходимо показать поверх контента формы
  // Messages component - memoized to prevent re-renders
  const Messages = React.useMemo(() => {
    const MessagesComponent = () => (
      <>
        {error && (
          <Alert
            icon={<IconX size='1rem' />}
            title='Ошибка'
            color='red'
            withCloseButton
            onClose={() => setError(null)}
            mb='md'
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            icon={<IconCheck size='1rem' />}
            title='Успех'
            color='green'
            withCloseButton
            onClose={() => setSuccess(null)}
            mb='md'
          >
            {success}
          </Alert>
        )}
      </>
    );

    return MessagesComponent;
  }, [error, success]); // Recreate when error or success changes

  return {
    ...form,
    handleSubmit,
    isSubmitting,
    error,
    success,
    setError,
    setSuccess,
    clearMessages,
    reset,
    setInitialData,
    Field: fieldComponents,
    buttons,
    Messages,
    showValidationErrors,
  };
}
